import React from 'react';
import { StudentState, Q, VQ } from '../../../store/student/types';
import { SummaryRow } from '../../Summary/SummaryRow';
import { boolToYesNo, formatCurrency } from '../../../utilities/formatting';
import {
  IOtherName,
  IAdditionalPassport,
  IFamilyMember,
  ITripToUk,
  IStudentVisa,
  IBorderRefusal,
  IExclusionOrder,
  OtherVisa,
  ISponsorDetails,
  IDocument,
  StudentVisaDocuments,
  IOtherNationality,
  UKVisaRefusal,
  NonUKVisaRefusal,
  OtherVisaDocs,
  CustomRequest,
  ICourse,
} from '../../../types';
import { getDurationFromDate } from '../../../utilities/helpers';
import { hasSpouseDetails } from '../../../utilities/pages';
import Finances from '../../sections/finance';
import VisaPrepAdditionalHistoryStep2Info from './additional-history/step-2';
import VisaPrepAdditionalHistoryStep5Info from './additional-history/step-5';
import VisaPrepAdditionalHistoryStep7Info from './additional-history/step-7';
import VisaPrepFamilyStep2 from './family/step-2';
import VisaPrepFamilyStep3 from './family/step-3';
import VisaPrepFamilyStep4 from './family/step-4';
import VisaPrepFamilyStep5 from './family/step-5';
import VisaPrepOtherTravellers from './other-travellers';
import VisaPrepUniEnrolmentStep3 from './uni-enrolment/step-3';
import Feedback from './feedback';
import './visaSummary.css';

import { passportStatusLabel } from '../../pages/personal-info/qPage7/utils';
import { UserRole } from '../../../store/auth/types';
import { hasPerm, P } from '../../../utilities/roles/permissions';
import { VisaRefusal } from 'types';
import { Box, Heading } from '@chakra-ui/react';
import { getVisaLabelFromValue } from 'data';
import { useTranslation } from "react-i18next";

type IProps = StudentState & { sponsor: ISponsorDetails; userRole: UserRole };

function VisaPrepPreview(props: IProps) {
    const { t } = useTranslation('forms.visa');
  const renderDocs = (docs: Array<IDocument>) => {
    if (docs === undefined || (docs && docs.length === 0)) {
      return null;
    }

    return docs.map((doc: IDocument, i: number) => (
      <React.Fragment key={`${doc.label}-${i}`}>
        {doc.fileNames &&
          doc.fileNames.map((fileName: string, k: number) => (
            <SummaryRow
              className={'visa-summary'}
              key={`file-${fileName}-${k}`}
              value={fileName}
              label={t('forms.visa:text', { label: doc.label, i: i + 1, k: k + 1 })}
            />
          ))}
      </React.Fragment>
    ));
  };

  const showRisks = hasPerm(props.userRole, P.CHECKLIST_FLAGS);

  return (
    <>
      <SummaryRow
        className={'visa-summary'}
        key={Q.COUNTRY}
        value={props.personalInfo.country}
        label={t('forms.visa:country')}
        fullLabel={''}
      />
      <SummaryRow className={'visa-summary'} key={Q.EMAIL} value={props.core.email} label={t('forms.visa:email')} fullLabel={''} />
      <SummaryRow
        className={'visa-summary'}
        key={Q.PHONE_NUMBER}
        value={props.personalInfo.phoneNumber}
        label={t('forms.visa:phoneNumber')}
        fullLabel={''}
      />
      <SummaryRow
        className={'visa-summary'}
        key={Q.GIVEN_NAME}
        value={props.personalInfo.givenName}
        label={t('forms.visa:givenNameS')}
        fullLabel={''}
      />
      <SummaryRow
        className={'visa-summary'}
        key={Q.SURNAME}
        value={props.personalInfo.surname}
        label={t('forms.visa:familyName')}
        fullLabel={''}
      />
      <SummaryRow
        className={'visa-summary'}
        key={Q.NATIONALITY}
        value={props.personalInfo.nationality}
        label={t('forms.visa:nationality')}
        fullLabel={''}
      />

      <SummaryRow
        className={'visa-summary'}
        key={Q.IS_COUNTRY_OF_NATIONALITY}
        value={boolToYesNo(props.personalInfo.applyingFromHomeCountry)}
        label={t('forms.visa:applyingFromHomeCountry')}
        fullLabel={''}
      />
      {props.personalInfo.countryOfVisaApplication && (
        <SummaryRow
          className={'visa-summary'}
          key={Q.COUNTRY_OF_VISA_APPLICATION}
          value={props.personalInfo.countryOfVisaApplication}
          label={t('forms.visa:applyingForVisaInCountry')}
          fullLabel={''}
        />
      )}

      <br />
      <SummaryRow
        className={'visa-summary'}
        key={Q.USE_WHATSAPP}
        value={props.personalInfo.useWhatsapp}
        label={t('forms.visa:doYouUseWhatsapp')}
        fullLabel={''}
      />
      {
        (props.personalInfo.useWhatsapp !== 'No',
        props.personalInfo.whatsAppPhoneNumber !== '' && (
          <SummaryRow
            className={'visa-summary'}
            key={Q.WHATSAPP_OTHER_NUMBER}
            value={props.personalInfo.whatsAppPhoneNumber}
            label={t('forms.visa:whatsappNumber')}
            fullLabel={''}
          />
        ))
      }
      <br />
      {props.offer.casReceived !== undefined && (
        <>
          <SummaryRow
            className={'visa-summary'}
            key={Q.CAS_RECEIVED}
            value={boolToYesNo(props.offer.casReceived!)}
            label={t('forms.visa:haveYouReceivedYouCasYet')}
            fullLabel={''}
          />
          {props.offer.casReceived && props.visa.studiesConfirmationNumber !== '' ? (
            <SummaryRow
              className={'visa-summary'}
              key={VQ.STUDIES_CONFIRMATION_NUMBER}
              value={props.visa.studiesConfirmationNumber}
              label={t('forms.visa:casNumber')}
              fullLabel={'What is your Confirmation of Acceptance for Studies Number?'}
            />
          ) : null}
        </>
      )}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:otherNames')}
                    </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={`${Q.HAS_OTHER_NAMES}`}
        value={boolToYesNo(props.personalInfo.hasOtherNames)}
        label={t('forms.visa:hasOtherNames')}
        fullLabel={
          'In addition to the names already provided, are you now or have you ever been known by another name?'
        }
      />
      <SummaryRow
        className={'visa-summary'}
        value={props.personalInfo.otherNames?.length}
        label={t('forms.visa:howMany')}
        fullLabel={'How many names?'}
      />
      {props.personalInfo.otherNames?.map((otherName: IOtherName, i: number) => (
        <React.Fragment key={`other-names-${i}`}>
          <SummaryRow
            className={'visa-summary'}
            key={`${Q.GIVEN_NAME}-${i}`}
            value={otherName.givenName}
            label={t('forms.visa:previousGivenName', { i: i + 1 })}
          />
          <SummaryRow
            className={'visa-summary'}
            key={`${Q.SURNAME}-${i}`}
            value={otherName.surname}
            label={t('forms.visa:previousFamilyName', { i: i + 1 })}
          />

          <SummaryRow
            className={'visa-summary'}
            key={`${Q.OTHER_NAME_REASON}-${i}`}
            value={otherName.reason}
            label={t('forms.visa:reasonForChange', { i: i + 1 })}
          />
        </React.Fragment>
      ))}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:genderAndMaritalStatus')}
                    </Heading>
      <SummaryRow className={'visa-summary'} key={Q.GENDER} value={props.personalInfo.gender} label={t('forms.visa:gender')} />
      {props.visa.maritalStatus && (
        <SummaryRow
          className={'visa-summary'}
          key={VQ.MARITAL_STATUS}
          value={props.visa.maritalStatus!.toLowerCase()}
          label={t('forms.visa:whatIsYourMaritalStatus')}
        />
      )}
      <br />
      <SummaryRow
        className={'visa-summary'}
        key={Q.FAMILY_MEMBERS_TRAVELLING}
        value={boolToYesNo(props.offer.hasDependents)}
        label={t('forms.visa:doYouHaveAnyoneComingWithYouToTh')}
      />
      <SummaryRow
        className={'visa-summary'}
        key={Q.SPOUSE_TRAVELLING}
        value={boolToYesNo(props.offer.hasPartner)}
        label={t('forms.visa:areYouApplyingForYourStudentVisa')}
      />
      {props.visa.maritalStatus !== undefined && hasSpouseDetails(props.visa.maritalStatus) && (
        <Box key={'partner'}>
          <Heading as="h4" fontSize={'md'}>
            {t('forms.visa:partnerDetails')}
                                </Heading>
          <SummaryRow
            className={'visa-summary'}
            key={VQ.SPOUSE_GIVEN_NAME}
            value={props.visa.spouseGivenName}
            label={t('forms.visa:partnerGivenName')}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.SPOUSE_FAMILY_NAME}
            value={props.visa.spouseFamilyName}
            label={t('forms.visa:partnerFamilyName')}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.SPOUSE_DOB}
            value={props.visa.spouseDob}
            label={t('forms.visa:partnerDateOfBirth')}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.SPOUSE_NATIONALITY}
            value={props.visa.spouseNationality}
            label={t('forms.visa:partnerNationality')}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.SPOUSE_SAME_NATIONALITY}
            value={boolToYesNo(props.visa.spouseSameNationality)}
            label={t('forms.visa:haveTheyAlwaysHadTheSameNational')}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.SPOUSE_LIVING_WITH}
            value={boolToYesNo(props.visa.spouseLivingWith)}
            label={t('forms.visa:doesYourSpousePartnerCurrentlyLi')}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.SPOUSE_TRAVELLING_WITH}
            value={props.visa.spouseTravellingWith}
            label={t('forms.visa:isThisPersonTravellingWithYou')}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.SPOUSE_HAS_PASSPORT}
            value={boolToYesNo(props.visa.spouseHasPassport)}
            label={t('forms.visa:doTheyHaveAPassportOrTravelDocum')}
          />
          {props.visa.spouseHasPassport && (
            <SummaryRow
              className={'visa-summary'}
              key={VQ.SPOUSE_DOB}
              value={props.visa.spousePassportNumber}
              label={t('forms.visa:currentPassportOrTravelDocumentN')}
            />
          )}
          <SummaryRow
            className={'visa-summary'}
            key={VQ.SPOUSE_UK_VISA}
            value={boolToYesNo(props.visa.spouseUkVisa)}
            label={t('forms.visa:doTheyHaveAVisaForTheUk')}
          />
        </Box>
      )}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:address')}
                    </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={Q.HOME_ADDRESS}
        value={props.personalInfo.homeAddress}
        label={t('forms.visa:permanentResidentialAddress')}
      />
      <SummaryRow className={'visa-summary'} key={Q.CITY} value={props.personalInfo.city} label={t('forms.visa:city')} />
      <SummaryRow
        className={'visa-summary'}
        key={Q.POSTCODE}
        value={props.personalInfo.postCode}
        label={t('forms.visa:postcodeIfYouHaveOne')}
      />
      <SummaryRow className={'visa-summary'} key={Q.COUNTRY} value={props.personalInfo.country} label={t('forms.visa:country')} />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.RESIDENTIAL_ADDRESS_START_DATE}
        value={props.visa.residentialAddressStartDate}
        label={t('forms.visa:residentialStartDate')}
      />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HOME_OWNERSHIP_STATUS}
        value={props.visa.homeOwnershipStatus}
        label={t('forms.visa:whatIsTheOwnershipStatusOfYourHo')}
      />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_DIFFERENT_CONTACT_DETAILS}
        value={boolToYesNo(props.visa.hasDifferentContactDetails)}
        label={t('forms.visa:differentPreferredContactDetails')}
        fullLabel={'Are your preferred contact details different from the contact details already provided above?'}
      />
      {props.visa.hasDifferentContactDetails && (
        <>
          <SummaryRow
            className={'visa-summary'}
            key={VQ.DIFF_CONTACT_ADDRESS}
            value={props.visa.diffContactAddress}
            label={t('forms.visa:contactAddress')}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.DIFF_CONTACT_CITY}
            value={props.visa.diffContactCity}
            label={t('forms.visa:city')}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.DIFF_CONTACT_POSTCODE}
            value={props.visa.diffContactPostcode}
            label={t('forms.visa:postcodeIfYouHaveOne')}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.DIFF_CONTACT_COUNTRY}
            value={props.visa.diffContactCountry}
            label={t('forms.visa:country')}
          />
        </>
      )}
      <SummaryRow
        className={'visa-summary'}
        key={VQ.RESIDENTIAL_ADDRESS_START_DATE}
        value={getDurationFromDate(props.visa.residentialAddressStartDate)}
        label={t('forms.visa:howLongHaveYouLivedAtThisAddress')}
      />
      {props.visa.homeOwnershipStatus === 'Other' && (
        <SummaryRow
          className={'visa-summary'}
          key={VQ.HOME_OWNERSHIP_STATUS_OTHER}
          value={props.visa.homeOwnershipStatusOther}
          label={t('forms.visa:other')}
          fullLabel={`Give more details about your living situation, such as who you live with and who owns the property`}
        />
      )}
      {/* TODO: delete it if we not need that anymore */}
      {/* <SummaryRow
        className={'visa-summary'}
        key={Q.APPLYING_FROM_HOME_COUNTRY}
        value={props.history.home_country_application}
        label={
          'Are you applying from the country where you are currently living?'
        }
      />
      <SummaryRow
        className={'visa-summary'}
        key={Q.APPLYING_FROM_NON_HOME_COUNTRY_REASON}
        value={props.history.home_country_application_reason}
        label={
          'Please tell us why you are applying from a country other than where you are currently living?'
        }
      /> */}
      <SummaryRow
        className={'visa-summary'}
        key={VQ.NON_HOME_COUNTRY_APP_DESC}
        value={props.visa.nonHomeCountryApplicationDesc}
        label={t('forms.visa:immigrationStatusAndExpiryDate')}
        fullLabel={
          'Please explain your visa/immigration status for the country you are applying from, and the expiry date of any visas you hold for this country'
        }
      />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:passport')}
                    </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={Q.PASSPORT_STATUS}
        value={passportStatusLabel(props.personalInfo.passportStatus)}
        label={t('forms.visa:doYouCurrentlyHaveAPassport')}
      />
      {props.documents.passport &&
      (props.documents.passport as any).length &&
      (props.documents.passport as any)[0].docNumber ? (
        <Box key={'passports'}>
          <Heading as="h4" fontSize={'md'}>
            {t('forms.visa:passports')}
                                    </Heading>
          {Object.values(props.documents.passport).map((pPort, i: number) => (
            <React.Fragment key={`passport-${i}`}>
              <SummaryRow
                className={'visa-summary'}
                key={`passport-number-${i}`}
                value={pPort.docNumber}
                label={t('forms.visa:passportPassportOrTravelDocument', { i: i + 1 })}
              />
              <SummaryRow
                className={'visa-summary'}
                key={`passport-issuing-auth-${i}`}
                value={pPort.placeOfIssue}
                label={t('forms.visa:passportIssuingAuthority', { i: i + 1 })}
              />
              <SummaryRow
                className={'visa-summary'}
                key={`passport-date-of-issue-${i}`}
                value={pPort.dateOfIssue}
                label={t('forms.visa:passportDateOfIssue', { i: i + 1 })}
              />
              <SummaryRow
                className={'visa-summary'}
                key={`passport-date-of-expiry-${i}`}
                value={pPort.dateOfExpiry}
                label={t('forms.visa:passportDateOfExpiry', { i: i + 1 })}
              />
            </React.Fragment>
          ))}
        </Box>
      ) : null}
      {props.personalInfo.passportStatus === 'Yes' && (
        <SummaryRow
          className={'visa-summary'}
          key={`passport-expiry`}
          value={props.personalInfo.passportExpiryDate}
          label={t('forms.visa:passportExpiryDate')}
        />
      )}
      {props.personalInfo.passportStatus !== 'Yes' && (
        <SummaryRow
          className={'visa-summary'}
          key={`passport-expected`}
          value={props.personalInfo.passportExpectedDate}
          label={t('forms.visa:whenDoYouExpectToReceiveYourPass')}
        />
      )}
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_ADDITIONAL_PASSPORTS}
        value={boolToYesNo(props.visa.hasAdditionalPassports)}
        label={t('forms.visa:hasAdditionalPassports')}
        fullLabel={`Have you had any other passports in the last 10 years? (other than the ones in the document upload section)`}
      />
      {props.visa.hasAdditionalPassports && (
        <>
          <SummaryRow
            className={'visa-summary'}
            value={props.visa.additionalPassports?.length}
            label={t('forms.visa:howManyAdditionalPassports')}
          />
          <Box key={'Additional passports'}>
            <Heading as="h4" fontSize={'md'}>
              {t('forms.visa:additionalPassports')}
                                      </Heading>
            {props.visa.additionalPassports?.map((pPort: IAdditionalPassport, i: number) => (
              <React.Fragment key={`additional-passports-${i}`}>
                <SummaryRow
                  className={'visa-summary'}
                  key={`${VQ.ADDITIONAL_PASSPORT_PASSPORT_NUMBER}-${i}`}
                  value={pPort.docNumber}
                  label={t('forms.visa:additionalPassportCurrentPasspor', { i: i + 1 })}
                />
                <SummaryRow
                  className={'visa-summary'}
                  key={`${VQ.ADDITIONAL_PASSPORT_ISSUING_AUTHORITY}-${i}`}
                  value={pPort.issuingAuthority}
                  label={t('forms.visa:additionalPassportIssuingAuthori', { i: i + 1 })}
                />
                <SummaryRow
                  className={'visa-summary'}
                  key={`${VQ.ADDITIONAL_PASSPORT_DATE_OF_ISSUE}-${i}`}
                  value={pPort.issueDate}
                  label={t('forms.visa:additionalPassportDateOfIssue', { i: i + 1 })}
                />
                <SummaryRow
                  className={'visa-summary'}
                  key={`${VQ.ADDITIONAL_PASSPORT_DATE_OF_EXPIRY}-${i}`}
                  value={pPort.expiryDate}
                  label={t('forms.visa:additionalPassportDateOfExpiry', { i: i + 1 })}
                />
              </React.Fragment>
            ))}
          </Box>
        </>
      )}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:nationalityCountryAndPlaceOfBirt')}
                    </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={Q.NATIONALITY}
        value={props.personalInfo.nationality}
        label={t('forms.visa:nationality')}
      />
      <SummaryRow
        className={'visa-summary'}
        key={Q.BIRTH_COUNTRY}
        value={props.personalInfo.birthCountry}
        label={t('forms.visa:countryOfBirth')}
      />
      <SummaryRow className={'visa-summary'} key={Q.DOB} value={props.personalInfo.dob} label={t('forms.visa:dateOfBirth')} />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:otherNationalities')}
                    </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={Q.HAS_OTHER_NATIONALITY}
        value={boolToYesNo(props.personalInfo.hasOtherNationality)}
        label={t('forms.visa:otherNationalityCitizenship')}
        fullLabel={'Do you currently hold, or have you ever held, any other nationality or citizenship?'}
      />
      {props.personalInfo.hasOtherNationality ? (
        <>
          <SummaryRow
            className={'visa-summary'}
            value={props.personalInfo.otherNationalities?.length}
            label={t('forms.visa:howManyOtherNationalities')}
          />
          {props.personalInfo.otherNationalities?.map((otherNationality: IOtherNationality, i: number) => (
            <React.Fragment key={`other-nationality-${i}`}>
              <SummaryRow
                className={'visa-summary'}
                key={`${Q.OTHER_NATIONALITIES}-nationality-${i}`}
                label={t('forms.visa:otherNationality', { i: i + 1 })}
                value={otherNationality.nationality}
              />
              <SummaryRow
                className={'visa-summary'}
                key={`${Q.OTHER_NATIONALITIES}-isCurrent-${i}`}
                label={t('forms.visa:otherNationalityIsThisACurrentOr', { i: i + 1 })}
                value={boolToYesNo(otherNationality.isCurrent)}
              />
            </React.Fragment>
          ))}
        </>
      ) : null}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:englishLanguage')}
                    </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={VQ.LANGUAGE}
        value={props.visa.language}
        label={t('forms.visa:interviewLanguage')}
        fullLabel={'If UKVI need to interview you, what language would you like to use in the interview?'}
      />
      <br />
      <SummaryRow
        className={'visa-summary'}
        key={Q.HAS_ENGLISH_TEST}
        value={props.personalInfo.hasEnglishTest}
        label={t('forms.visa:englishTest')}
        fullLabel="Did you submit an English test as part of your application?"
      />
      {props.personalInfo.hasEnglishTest && (
        <SummaryRow
          className={'visa-summary'}
          key={Q.ENGLISH_TEST_TYPE}
          value={props.personalInfo.englishTestType}
          label={t('forms.visa:type')}
          fullLabel="What type of English test?"
        />
      )}
      {props.personalInfo.hasEnglishTest && props.personalInfo.englishTestType === 'Other' && (
        <SummaryRow
          className={'visa-summary'}
          key={Q.ENGLISH_TEST_TYPE_OTHER}
          value={props.personalInfo.englishTestTypeOther}
          label={t('forms.visa:other')}
        />
      )}
      {props.personalInfo.hasEnglishTest !== undefined && props.personalInfo.hasEnglishTest === false && (
        <Heading as="h4" fontSize={'md'}>
          {t('forms.visa:youHaveIndicatedThatYouHaveNotSu')}
                          </Heading>
      )}
      <SummaryRow
        className={'visa-summary'}
        key={VQ.QUALIFICATION_AS_PROOF_OF_ENGLISH}
        value={boolToYesNo(props.visa.qualificationAsProofOfEnglish)}
        label={t('forms.visa:areYouAreUsingAPreviousQualifica')}
      />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.QUALIFICATION_AND_COUNTRY}
        value={props.visa.qualificationAndCountry}
        label={t('forms.visa:pleaseStateYourAcademicQualifica')}
      />
      <SummaryRow
        className={'visa-summary'}
        key={`course-in-uk-tier-4`}
        value={boolToYesNo(
          props.history.hasPreviousUkStudy &&
            props.history.hasPreviousUkStudyVisa &&
            Number(props.history.previousUkStudyVisas?.length) > 0 &&
            props.history.previousUkStudyVisas?.find(x => x.visaType === 'General Student') !== undefined,
        )}
        label={t('forms.visa:haveYouSuccessfullyCompletedACou')}
      />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:dependants')}
                    </Heading>
      <VisaPrepFamilyStep5 {...props.visa} />
      <br />
      <SummaryRow
        className={'visa-summary'}
        key={Q.CHILDREN_TRAVELLING}
        label={t('forms.visa:applyingForAStudentVisaWithDepen')}
        fullLabel="Are you applying for your Student visa with dependant children?"
        value={boolToYesNo(props.offer.hasChildren)}
      />
      {props.offer.hasChildren && (
        <SummaryRow
          className={'visa-summary'}
          key="children_num"
          label={t('forms.visa:numOfChildren')}
          fullLabel="How many children?"
          value={props.offer.childrenNum}
        />
      )}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:parents')}
                    </Heading>
      <VisaPrepFamilyStep2 {...props.visa} />
      <br />
      <VisaPrepFamilyStep3 {...props.visa} />
      <br />
      <VisaPrepFamilyStep4 {...props.visa} />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:ukContactDetails')}
                    </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={VQ.UK_PRESENCE}
        label={t('forms.visa:doYouHaveAnEstablishedPresenceIn')}
        value={boolToYesNo(props.visa.ukPresence)}
      />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.UK_FAMILY}
        label={t('forms.visa:doYouHaveFamilyInTheUk')}
        value={boolToYesNo(props.visa.ukFamily)}
      />
      {props.visa.ukFamily && (
        <>
          <SummaryRow
            className={'visa-summary'}
            key={VQ.STAYING_WITH_FAMILY}
            label={t('forms.visa:willYouBeStayingWithAnyFamilyMem')}
            value={boolToYesNo(props.visa.stayingWithFamily)}
          />
          {props.visa.stayingWithFamily && (
            <>
              <SummaryRow
                className={'visa-summary'}
                label={t('forms.visa:howManyFamilyMembersWillYouBeSta')}
                value={props.visa.familyMembers?.length}
              />
              <Box key={'Family members'}>
                <Heading as="h4" fontSize={'md'}>
                  {t('forms.visa:familyMembers')}
                                                  </Heading>
                {props.visa.familyMembers?.map((familyMember: IFamilyMember, i: number) => (
                  <React.Fragment key={`family-members-${i}`}>
                    <SummaryRow
                      className={'visa-summary'}
                      key={`${VQ.NATIONALITY}-${i}`}
                      value={familyMember.nationality}
                      label={t('forms.visa:familyMemberNationality', { i: i + 1 })}
                    />
                    <SummaryRow
                      className={'visa-summary'}
                      key={`${VQ.FAMILY_NAME}`}
                      value={familyMember.familyName}
                      label={t('forms.visa:familyMemberFamilyName', { i: i + 1 })}
                    />
                    <SummaryRow
                      className={'visa-summary'}
                      key={`${VQ.GIVEN_NAME}`}
                      value={familyMember.givenName}
                      label={t('forms.visa:familyMemberGivenName', { i: i + 1 })}
                    />
                    <SummaryRow
                      className={'visa-summary'}
                      key={VQ.DOB}
                      label={t('forms.visa:familyMemberDateOfBirth', { i: i + 1 })}
                      value={familyMember.dob}
                    />
                  </React.Fragment>
                ))}
              </Box>
              <SummaryRow
                className={'visa-summary'}
                key={VQ.FAMILY_CONTACT_ADDRESS}
                label={t('forms.visa:contactAddressForFamilyYouAreSta')}
                value={props.visa.familyContactAddress}
              />
              <SummaryRow
                className={'visa-summary'}
                key={VQ.FAMILY_CONTACT_CITY}
                label={t('forms.visa:city')}
                value={props.visa.familyContactCity}
              />
              <SummaryRow
                className={'visa-summary'}
                key={VQ.FAMILY_CONTACT_POSTCODE}
                label={t('forms.visa:postcode')}
                value={props.visa.familyContactPostcode}
              />
              <SummaryRow
                className={'visa-summary'}
                key={VQ.FAMILY_CONTACT_COUNTRY}
                label={t('forms.visa:country')}
                value={props.visa.familyContactCountry}
              />
              <SummaryRow
                className={'visa-summary'}
                key={VQ.FAMILY_CONTACT_NUMBER}
                label={t('forms.visa:contactNumber')}
                value={props.visa.familyPhoneNumber}
              />
              <SummaryRow
                className={'visa-summary'}
                key={VQ.FAMILY_CONTACT_EMAIL}
                label={t('forms.visa:contactEmailAddress')}
                value={props.visa.familyEmail}
              />
            </>
          )}
        </>
      )}
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_OTHER_CONTACT_ADDRESS}
        label={t('forms.visa:doYouHaveAnotherAddressInTheUk')}
        value={boolToYesNo(props.visa.hasOtherContactAddress)}
      />
      {props.visa.hasOtherContactAddress && (
        <>
          <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
            {t('forms.visa:plannedNonFamilyAddress')}
                                </Heading>
          <SummaryRow
            className={'visa-summary'}
            key={VQ.OTHER_CONTACT_ADDRESS}
            label={t('forms.visa:ukContactAddress')}
            value={props.visa.otherContactAddress}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.OTHER_CONTACT_CITY}
            label={t('forms.visa:city')}
            value={props.visa.otherContactCity}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.OTHER_CONTACT_POSTCODE}
            label={t('forms.visa:postcode')}
            value={props.visa.otherContactPostcode}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.OTHER_CONTACT_COUNTRY}
            label={t('forms.visa:country')}
            value={props.visa.otherContactCountry}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.OTHER_CONTACT_NUMBER}
            label={t('forms.visa:contactNumberIfApplicable')}
            value={props.visa.otherPhoneNumber}
          />
        </>
      )}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:otherTravellers')}
                    </Heading>
      <VisaPrepOtherTravellers {...props.visa} />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:ukTravel')}
                    </Heading>
      <SummaryRow
        className={'visa-summary'}
        key="overseasTravel"
        label={t('forms.visa:overseasTravel')}
        fullLabel="Have you ever travelled outside of your home country in the last 10 years?"
        value={boolToYesNo(props.history.hasOverseasTravel)}
      />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_BEEN_TO_UK}
        label={t('forms.visa:haveYouEverBeenToTheUk')}
        value={boolToYesNo(props.visa.hasBeenToUk)}
      />
      <SummaryRow
        className={'visa-summary'}
        key="ukTravel"
        label={t('forms.visa:travelledToTheUkInTheLast10Years')}
        fullLabel="Have you ever travelled to the UK in the last 10 years?"
        value={boolToYesNo(props.history.hasOverseasTravel && props.history.hasUkTravel)}
      />
      {props.history.hasOverseasTravel && props.history.hasUkTravel && (
        <SummaryRow
          className={'visa-summary'}
          key="ukTravelTimes"
          label={t('forms.visa:howManyTimes')}
          fullLabel="Approximately how many times?"
          value={props.history.ukTravelTimes}
        />
      )}
      {props.visa.tripsToUk?.map((trip: ITripToUk, i: number) => (
        <React.Fragment key={`uk-trip-${i}`}>
          <SummaryRow
            className={'visa-summary'}
            key={`${VQ.TRIPS_TO_UK_ARRIVAL_DATE}-${i}`}
            label={t('forms.visa:ukTripDateOfArrival', { i: i + 1 })}
            value={trip.arrivalDate}
          />
          <SummaryRow
            className={'visa-summary'}
            key={`${VQ.TRIPS_TO_UK_DEPARTURE_DATE}-${i}`}
            label={t('forms.visa:ukTripDateOfDeparture', { i: i + 1 })}
            value={trip.departureDate}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.TRIPS_TO_UK_PURPOSE}
            value={trip.purpose}
            label={t('forms.visa:ukTripPurposeOfTrip', { i: i + 1 })}
          />
        </React.Fragment>
      ))}
      <br />
      <SummaryRow
        className={'visa-summary'}
        key="prevUKStudy"
        label={t('forms.visa:previousUkStudy')}
        fullLabel="Have you studied in the UK before?"
        value={boolToYesNo(props.history.hasPreviousUkStudy)}
      />
      <SummaryRow
        className={'visa-summary'}
        key="prevUKStudyVisa"
        label={t('forms.visa:ukStudyVisa')}
        fullLabel="Did you receive a student visa for your previous UK study?"
        value={boolToYesNo(props.history.hasPreviousUkStudyVisa)}
      />
      {props.history.hasPreviousUkStudyVisa && props.history.hasPreviousUkStudy && (
        <SummaryRow
          className={'visa-summary'}
          key="prevUKStudyVisaNumber"
          label={t('forms.visa:numberOfVisas')}
          fullLabel="How many UK student visas have you received?"
          value={props.history.previousUkStudyVisas?.length}
        />
      )}
      {props.history.previousUkStudyVisas?.map((visa: IStudentVisa, i: number) => (
        <React.Fragment key={`previous-visa-${i}`}>
          <SummaryRow
            className={'visa-summary'}
            key={`type-${i}`}
            label={t('forms.visa:studentVisaWhatTypeOfStudentVisa', { i: i + 1 })}
            value={getVisaLabelFromValue(visa.visaType)}
          />
          <SummaryRow
            className={'visa-summary'}
            key={`level-${i}`}
            label={t('forms.visa:studentVisaWhatWasTheLevelOfStud', { i: i + 1 })}
            value={visa.level}
          />
          <SummaryRow
            className={'visa-summary'}
            key={`year-${i}`}
            label={t('forms.visa:studentVisaWhatWasTheYearYouRece', { i: i + 1 })}
            value={visa.year}
          />
          <SummaryRow
            className={'visa-summary'}
            key={`complete-${i}`}
            label={t('forms.visa:studentVisaDidYouCompleteYourCou', { i: i + 1 })}
            value={boolToYesNo(visa.isComplete)}
          />
          {visa.isComplete === false && (
            <SummaryRow
              className={'visa-summary'}
              key={`incomplete-${i}`}
              label={t('forms.visa:studentVisaWhyDidYouNotCompleteY', { i: i + 1 })}
              value={visa.isIncompleteRecorded}
            />
          )}
          <SummaryRow
            className={'visa-summary'}
            key={`on-record-${i}`}
            label={t('forms.visa:studentVisaWasYourVisaCurtailedS', { i: i + 1 })}
            value={visa.isVisaShortened}
          />
          {visa.isVisaShortened && (
            <SummaryRow
              className={'visa-summary'}
              key={`curtailed-${i}`}
              label={t('forms.visa:studentVisaWhatDateWasYourVisaCu', { i: i + 1 })}
              value={visa.shortenedDate}
            />
          )}
        </React.Fragment>
      ))}
      <SummaryRow
        className={'visa-summary'}
        key={Q.PREV_UK_OTHER_VISA}
        label={t('forms.visa:otherUkVisas')}
        fullLabel="Have you been granted any other visas (non-student) for the UK in the last 10 years?"
        value={boolToYesNo(props.history.hasOtherUkVisas)}
      />
      {props.history.hasOtherUkVisas && (
        <>
          <SummaryRow className={'visa-summary'} label={t('forms.visa:howMany2')} value={props.history.otherUkVisas?.length} />
          {props.history.otherUkVisas?.map((visa: OtherVisa, i: number) => (
            <React.Fragment key={`previous-uk-visa-${i}`}>
              <SummaryRow
                className={'visa-summary'}
                label={t('forms.visa:visaWhatTypeOfVisaWereYouGranted', { i })}
                value={getVisaLabelFromValue(visa.visaType)}
              />
              <SummaryRow
                className={'visa-summary'}
                label={t('forms.visa:visaWhatYearWereYouGrantedThisVi', { i })}
                value={visa.year}
              />
              <SummaryRow
                className={'visa-summary'}
                label={t('forms.visa:visaIsVisaCompleted', { i })}
                value={boolToYesNo(visa.isComplete)}
              />
            </React.Fragment>
          ))}
        </>
      )}
      <br />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_RECEIVED_MEDICAL_TREATMENT}
        label={t('forms.visa:haveYouEverReceivedMedicalTreatm')}
        value={boolToYesNo(props.visa.hasReceivedMedicalTreatment)}
      />
      {props.visa.leaveToRemains && props.visa.leaveToRemains.length > 0 && (
        <>
          <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
            {t('forms.visa:leaveToRemain')}
                                </Heading>
          <VisaPrepAdditionalHistoryStep5Info {...props.visa} />
        </>
      )}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:previousUkDocuments')}
                    </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_NI_NUMBER}
        label={t('forms.visa:doYouHaveAUkNationalInsuranceNum')}
        value={boolToYesNo(props.visa.hasNINumber)}
      />
      {props.visa.hasNINumber && (
        <SummaryRow
          className={'visa-summary'}
          key={VQ.NI_NUMBER}
          value={props.visa.niNumber}
          label={t('forms.visa:whatIsTheNumber')}
        />
      )}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:commonwealthAndEeaTravel')}
                    </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_TRAVELLED}
        label={t('forms.visa:hasTravelledOutsideResidence')}
        fullLabel={'Have you ever travelled outside your country of residence, excluding the UK, in the last 10 years?'}
        value={boolToYesNo(props.visa.hasTravelled)}
      />
      <VisaPrepAdditionalHistoryStep2Info {...props.visa} />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:arrival')}
                    </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={VQ.ARRIVAL_DATE}
        label={t('forms.visa:dateOfPlannedArrivalInTheUk')}
        value={props.visa.arrivalDate}
      />
      <SummaryRow
        className={'visa-summary'}
        title={t('forms.visa:howLongDoYouIntendToStayInTheUk')}
        key={VQ.LENGTH_IN_UK_INTENTION_DD}
        label={t('forms.visa:days')}
        value={`${props.visa.lengthOfStayDays || 0}`}
      />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.LENGTH_IN_UK_INTENTION_MM}
        label={t('forms.visa:months')}
        value={`${props.visa.lengthOfStayMonths || 0}`}
      />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.LENGTH_IN_UK_INTENTION_YY}
        label={t('forms.visa:years')}
        value={`${props.visa.lengthOfStayYears || 0}`}
      />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:ukLeaveRefusalsAndExclusions')}
                    </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={Q.PREVIOUS_VISA_REJECTION}
        label={t('forms.visa:borderRefusals')}
        fullLabel="Have you been refused entry to the UK at the border in your lifetime?"
        value={boolToYesNo(props.history.hasBorderRefusal)}
      />
      <SummaryRow
        className={'visa-summary'}
        label={t('forms.visa:numberOfBorderRefusals')}
        value={props.visa.borderRefusals?.length}
      />
      {props.visa.borderRefusals?.map((borderRefusal: IBorderRefusal, i: number) => (
        <>
          <React.Fragment key={`refusal-${i}`}>
            <SummaryRow
              className={'visa-summary'}
              key={`${VQ.BORDER_REFUSAL_DATE}-${i}`}
              label={t('forms.visa:borderRefusalDateOfRefusal', { i: i + 1 })}
              value={borderRefusal.refusalDate}
            />
            <SummaryRow
              className={'visa-summary'}
              key={`${VQ.BORDER_REFUSAL_AIRPORT}-${i}`}
              value={borderRefusal.airport}
              label={t('forms.visa:borderRefusalPortOrAirport', { i: i + 1 })}
            />
            <SummaryRow
              className={'visa-summary'}
              key={`${VQ.BORDER_REFUSAL_REASON}-${i}`}
              value={borderRefusal.reason}
              label={t('forms.visa:borderRefusalReason', { i: i + 1 })}
            />
            <SummaryRow
              className={'visa-summary'}
              key={`${VQ.BORDER_REFUSAL_REFERENCE}-${i}`}
              value={borderRefusal.reference}
              label={t('forms.visa:borderRefusalReferenceNumber', { i: i + 1 })}
            />
          </React.Fragment>
        </>
      ))}
      <br />
      <SummaryRow
        className={'visa-summary'}
        key={Q.PREV_VISA_REFUSAL}
        label={t('forms.visa:visaRefusals')}
        fullLabel="Have you ever previously received a visa refusal when attempting to travel to the UK in the last 10 years?"
        value={boolToYesNo(props.history.hasUkVisaRefusals)}
      />
      <SummaryRow
        className={'visa-summary'}
        key="visaRefusalNumber"
        label={t('forms.visa:number')}
        fullLabel="How many times?"
        value={props.history.ukVisaRefusals?.length}
      />
      {props.history.ukVisaRefusals?.map((visa: OtherVisa, i: number) => (
        <React.Fragment key={`previous-uk-visa-refusal-${i}`}>
          <SummaryRow
            className={'visa-summary'}
            key={`${Q.PREVIOUS_VISA_REJECTION}-type-${i}`}
            label={t('forms.visa:visaWhatTypeOfVisaWereYouGranted', { i })}
            value={getVisaLabelFromValue(visa.visaType)}
          />
          <SummaryRow
            className={'visa-summary'}
            key={`${Q.PREVIOUS_VISA_REJECTION}-year-${i}`}
            label={t('forms.visa:visaWhatYearWereYouGrantedThisVi', { i })}
            value={visa.year}
          />
          <SummaryRow
            className={'visa-summary'}
            key={`${Q.PREVIOUS_VISA_REJECTION}-complete-${i}`}
            label={t('forms.visa:visaIsVisaCompleted', { i })}
            value={boolToYesNo(visa.isComplete)}
          />
        </React.Fragment>
      ))}
      <br />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_VOLUNTARILY_DEPARTED}
        label={t('forms.visa:haveYouEverVoluntarilyElectedToD')}
        value={boolToYesNo(props.visa.hasVoluntarilyDeparted)}
      />
      <br />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_EXCLUSION_ORDER}
        label={t('forms.visa:haveYouReceivedOrHaveYouBeenSubj')}
        value={boolToYesNo(props.visa.hasExclusionOrder)}
      />
      {props.visa.hasExclusionOrder && (
        <>
          <SummaryRow
            className={'visa-summary'}
            label={t('forms.visa:howManyExclusionOrders')}
            value={props.visa.exclusionOrders?.length}
          />
          <Box key={'exclusion-orders'}>
            <Heading as="h4" fontSize={'md'}>
              {t('forms.visa:exclusionOrders')}
                                      </Heading>
            {props.visa.exclusionOrders?.map((exclusion: IExclusionOrder, i: number) => (
              <React.Fragment key={`exclusion-${i}`}>
                <SummaryRow
                  className={'visa-summary'}
                  key={`${VQ.EXCLUSION_DATE}-${i}`}
                  label={t('forms.visa:exclusionOrderDateOfExclusion', { i: i + 1 })}
                  value={exclusion.exclusionDate}
                />
                <SummaryRow
                  className={'visa-summary'}
                  key={`${VQ.EXCLUSION_REASON}-${i}`}
                  value={exclusion.reason}
                  label={t('forms.visa:exclusionOrderReasonForExclusion', { i: i + 1 })}
                />
                <SummaryRow
                  className={'visa-summary'}
                  key={`${VQ.EXCLUSION_REFERENCE}-${i}`}
                  value={exclusion.reference}
                  label={t('forms.visa:exclusionOrderReferenceNumber', { i: i + 1 })}
                />
              </React.Fragment>
            ))}
          </Box>
        </>
      )}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:otherTravel')}
                    </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={Q.PREV_NON_UK_VISA_REFUSAL}
        label={t('forms.visa:nonUkVisaRefusals')}
        fullLabel="Have you received a visa refusal from any other country (non-UK) in the last 10 years?"
        value={boolToYesNo(props.history.hasOtherVisaRefusals)}
      />
      {props.history.hasOtherVisaRefusals && (
        <>
          <SummaryRow
            className={'visa-summary'}
            label={t('forms.visa:howManyTimes2')}
            value={props.history.otherVisaRefusals?.length}
          />
          {props.history.otherVisaRefusals?.map((visa: VisaRefusal, i: number) => (
            <React.Fragment key={`previous-non-uk-refusal-${i}`}>
              <SummaryRow
                className={'visa-summary'}
                label={t('forms.visa:nonUkVisaRefusalWhatCountryIssue', { i: i + 1 })}
                value={visa.country}
              />
              <SummaryRow
                className={'visa-summary'}
                label={t('forms.visa:nonUkVisaRefusalWasTheRefusalFor', { i: i + 1 })}
                value={boolToYesNo(visa.isStudentVisa)}
              />
              <SummaryRow
                className={'visa-summary'}
                label={t('forms.visa:nonUkVisaRefusalWhatYearDidYouRe', { i: i + 1 })}
                value={visa.year}
              />
            </React.Fragment>
          ))}
        </>
      )}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:deportations')}
                    </Heading>
      <VisaPrepAdditionalHistoryStep7Info {...props.visa} />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:criminalConvictions')}
                    </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={Q.HAS_CRIMINAL_CONVICTIONS}
        label={t('forms.visa:haveYouEverHadAnyCriminalConvict')}
        value={boolToYesNo(props.personalInfo.hasCriminalConvictions)}
      />
      {props.personalInfo.hasCriminalConvictions && (
        <SummaryRow
          className={'visa-summary'}
          key={Q.CRIMINAL_CONVICTIONS_REASON}
          label={t('forms.visa:criminalConvictionsInfo')}
          fullLabel="Please provide more information on the date and nature of the offence(s)"
          value={props.personalInfo.criminalConvictionReason || ''}
        />
      )}
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_CRIMINAL_CHARGES}
        label={t('forms.visa:hasCriminalCharges')}
        fullLabel={
          'Have you ever been charged in any country with a criminal offence for which you have not yet been tried in the court (including traffic offences)?'
        }
        value={boolToYesNo(props.visa.hasCriminalCharges)}
      />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_BAD_ACTIVITIES}
        label={t('forms.visa:badActivities')}
        fullLabel={
          'Have you engaged in any other activities that might indicate that you may not be admitted to the UK?'
        }
        value={boolToYesNo(props.visa.hasBadActivities)}
      />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:declarations')}
                    </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_WAR_CRIMES}
        label={t('forms.visa:warCrimes')}
        fullLabel={
          'In times of either peace or war have you ever been involved in, or suspected of involvement in, war crimes, crimes against humanity or genocide?'
        }
        value={boolToYesNo(props.visa.hasWarCrimes)}
      />
      <br />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_TERRORISM_INVOLVEMENT}
        label={t('forms.visa:terrorismInvolvement')}
        fullLabel={'Have you ever been involved in, supported or encouraged terrorist activities in any country?'}
        value={boolToYesNo(props.visa.hasTerrorismInvolvement)}
      />
      <br />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_TERRORISM_CONNECTION}
        label={t('forms.visa:terrorismConnection')}
        fullLabel={
          'Have you ever been a member of, or given support to, an organisation that has been connected with terrorism?'
        }
        value={boolToYesNo(props.visa.hasTerrorismConnection)}
      />
      <br />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_TERRORISM_JUSTIFIED}
        label={t('forms.visa:justifiedTerrorism')}
        fullLabel={
          'Have you ever, by any means or medium, expressed views that justify or glorify terrorist violence, or that may encourage others to commit acts of terrorism or other serious criminal acts?'
        }
        value={boolToYesNo(props.visa.hasTerrorismJustified)}
      />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:sponsorLicenceDetails')}
                    </Heading>
      <SummaryRow
        className={'visa-summary'}
        key="sponsor-licence"
        label={t('forms.visa:whatIsYourSponsorLicenceNumber')}
        value={props.sponsor.licenceNumber}
      />
      <SummaryRow
        className={'visa-summary'}
        key="sponsor-address"
        label={t('forms.visa:sponsorSAddress')}
        value={props.sponsor.address}
      />
      <SummaryRow className={'visa-summary'} key="sponsor-town" label={t('forms.visa:townCity')} value={props.sponsor.city} />
      <SummaryRow className={'visa-summary'} key="sponsor-postcode" label={t('forms.visa:postcode')} value={props.sponsor.postcode} />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:placeOfStudy')}
                    </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={VQ.STUDY_ADDRESS}
        label={t('forms.visa:whatIsTheAddressOfTheMainSiteOfS')}
        value={props.visa.studyAddress}
      />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:universityApplication')}
                    </Heading>
      <VisaPrepUniEnrolmentStep3 {...props.visa} />
      <SummaryRow
        className={'visa-summary'}
        key="ATAS-required"
        label={t('forms.visa:isAtasRequired')}
        value={boolToYesNo(!!(props.documents.atas && props.documents.atas.length))}
      />
      {props.documents.atas && props.documents.atas.length ? (
        <SummaryRow
          className={'visa-summary'}
          key="ATAS-applied"
          label={t('forms.visa:haveYouAppliedForYourAtasCertifi')}
          value={boolToYesNo(Object.values(props.documents.atas).find(x => x.isAtasPending === true) !== undefined)}
        />
      ) : null}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:funding')}
                    </Heading>
      {/* TODO
      <SummaryRow
        className={'visa-summary'}
        key="course-fee"
        label={'Course Fee'}
        value={`£${formatCurrency(props.financials.courseFee)}`}
      />
      <SummaryRow
        className={'visa-summary'}
        key="pre-sessional-fee"
        label={'Pre-Sessional Fee'}
        value={`£${formatCurrency(props.financials.preSessionalCourseFee)}`}
      />
      <SummaryRow
        className={'visa-summary'}
        key="living-cost"
        label={'Living cost'}
        value={`£${formatCurrency(
          props.financials.fundingSummary.studentLivingCost.amount
        )}`}
      />
      */}
      <SummaryRow
        className={'visa-summary'}
        key="dependants-living-cost"
        label={t('forms.visa:dependantsLivingCost')}
        value={`£${formatCurrency(
          (props.financials.fundingSummary?.spouseLivingCost.amount ?? 0) +
            (props.financials.fundingSummary?.childrenLivingCost.amount ?? 0),
        )}`}
      />
      <Finances
        showRisks={showRisks}
        organisationSponsorship={props.financials.organisationSponsorship ?? {}}
        scholarshipSponsorship={props.financials.scholarshipSponsorship ?? {}}
        educationSponsorship={props.financials.educationSponsorship ?? {}}
        familySponsorship={props.financials.familySponsorship ?? {}}
        otherSponsorship={props.financials.otherSponsorship ?? {}}
        risk={props.risks}
        courseFeeDepositReqd={props.financials.courseFeeDepositReqd ?? ''}
        courseFeeDepositDate={props.financials.courseFeeDepositDate ?? ''}
        courseFeeDeposit={`£${formatCurrency(props.financials.courseFeeDeposit ?? 0)}`}
        courseFeeDepositNotReqdReason={props.financials.courseFeeDepositNotReqdReason ?? ''}
        accomDepositReqd={props.financials.accomDepositReqd ?? ''}
        accomDepositDate={props.financials.accomDepositDate ?? ''}
        accomDeposit={`£${formatCurrency(props.financials.accomDeposit ?? 0)}`}
      />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:previousStudy')}
                    </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={Q.LAST_COURSE}
        label={t('forms.visa:whatWasTheLastCourseYouSuccessfu')}
        value={props.history.lastCourse}
      />
      {props.history.lastCourse === 'Other' && (
        <SummaryRow
          className={'visa-summary'}
          key={Q.LAST_COURSE_OTHER}
          label={t('forms.visa:name')}
          fullLabel="Please state why you chose other:"
          value={props.history.lastCourseOther}
        />
      )}
      <SummaryRow
        className={'visa-summary'}
        key={Q.LAST_COURSE_YEAR}
        label={t('forms.visa:year')}
        fullLabel="In which year did you complete this course?"
        value={props.history.lastCourseYear}
      />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:courseDetails')}
                    </Heading>
      <SummaryRow className={'visa-summary'} key="school-name" label={t('forms.visa:schoolName')} value={props.sponsor.schoolName} />
      {props.offer.courses?.map((course: ICourse, i: number) => (
        <React.Fragment key={`course-${i}`}>
          <SummaryRow
            className={'visa-summary'}
            key={`${Q.COURSES}-name-${i}`}
            label={t('forms.visa:courseName2', { i: i + 1 })}
            value={course.courseName}
          />
          <SummaryRow
            className={'visa-summary'}
            key={`${Q.COURSES}-start-date-${i}`}
            label={t('forms.visa:start', { i: i + 1 })}
            value={course.startDate}
          />
          <SummaryRow
            className={'visa-summary'}
            key={`${Q.COURSES}-end-date-${i}`}
            label={t('forms.visa:end', { i: i + 1 })}
            value={course.endDate}
          />
          <SummaryRow
            className={'visa-summary'}
            key={`${Q.COURSES}-fee-${i}`}
            label={t('forms.visa:fee2', { i: i + 1 })}
            value={course.courseFee}
          />
          <SummaryRow
            className={'visa-summary'}
            key={`${Q.COURSES}-is-work-placement-${i}`}
            label={t('forms.visa:isWorkPlacement2', { i: i + 1 })}
            value={course.isWorkPlacement}
          />
        </React.Fragment>
      ))}
      <br />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.ASSESSED_BY_SPONSOR}
        label={t('forms.visa:assessedBySponsor')}
        fullLabel={
          'Have you been assessed by the sponsor by other means, for example references or a portfolio of artwork?'
        }
        value={boolToYesNo(props.visa.assessedBySponsor)}
      />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:additionalInformation')}
                    </Heading>
      <Feedback {...props.visa} />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        {t('forms.visa:providedDocuments')}
                    </Heading>
      <Box key={'academic-documents'}>
        <Heading as="h4" fontSize={'md'}>
          {'Academic/Travel Documents'}
        </Heading>
        {renderDocs(Object.values(props.documents.cas))}
        {renderDocs(Object.values(props.documents.passport))}
        {renderDocs(Object.values(props.documents.academicCertificate))}
        {renderDocs(Object.values(props.documents.academicTranscript))}
        {Object.values(props.documents.studentVisas).map((studentVisa: StudentVisaDocuments) => {
          return (
            <>
              {renderDocs(Object.values(studentVisa.visa ?? {}))}
              {renderDocs(Object.values(studentVisa.cas ?? {}))}
              {renderDocs(Object.values(studentVisa.biometric ?? {}))}
            </>
          );
        })}
        {Object.values(props.documents.visaRefusals).map((visaRefusal: UKVisaRefusal) =>
          renderDocs(Object.values(visaRefusal.visas)),
        )}
        {Object.values(props.documents.nonUkVisaRefusals).map((nonUKVisaRefusal: NonUKVisaRefusal) =>
          renderDocs(Object.values(nonUKVisaRefusal.visas)),
        )}
        {Object.values(props.documents.otherVisas).map((otherVisa: OtherVisaDocs) =>
          renderDocs(Object.values(otherVisa.visa ?? {})),
        )}
        {renderDocs(Object.values(props.documents.parentalConsent))}
        {renderDocs(Object.values(props.documents.tbTest))}
        {renderDocs(Object.values(props.documents.atas))}
        {renderDocs(Object.values(props.documents.englishTest))}
        {renderDocs(Object.values(props.documents.postStudyLetter))}
        {Object.values(props.documents.customRequest).map((customRequestDocument: CustomRequest) =>
          renderDocs(Object.values(customRequestDocument.documents ?? {})),
        )}
      </Box>
      <Box key={'finance-documents'}>
        <Heading as="h4" fontSize={'md'}>
          {t('forms.visa:financialDocuments')}
                          </Heading>
        {/* {renderDocs(Object.values(props.financeDocuments.deposit.documents))}
        {Object.values(props.financeDocuments.familySponsorship).map(
          familySponsor => {
            return (
              <>
                {[
                  renderDocs(Object.values(familySponsor.bankStatement)),
                  renderDocs(Object.values(familySponsor.bankTranslation)),
                  renderDocs(Object.values(familySponsor.bankLetter)),
                  renderDocs(Object.values(familySponsor.parentLetter))
                ]}
              </>
            );
          }
        )}
        {Object.values(props.financeDocuments.birthCertificate).map(bCert => {
          return (
            <>
              {[
                renderDocs(Object.values(bCert.certificate)),
                renderDocs(Object.values(bCert.translation))
              ]}
            </>
          );
        })}
        {Object.values(props.financeDocuments.governmentSponsorship).map(
          govDocs => {
            return <>{renderDocs(Object.values(govDocs.documents))}</>;
          }
        )}
        {Object.values(props.financeDocuments.companySponsorship).map(
          compDocs => {
            return <>{renderDocs(Object.values(compDocs.documents))}</>;
          }
        )}
        {Object.values(props.financeDocuments.scholarshipSponsorship).map(
          scholDocs => {
            return <>{renderDocs(Object.values(scholDocs.documents))}</>;
          }
        )}
        {Object.values(props.financeDocuments.educationSponsorship).map(
          eduDocs => {
            return <>{renderDocs(Object.values(eduDocs.documents))}</>;
          }
        )}
        {Object.values(props.financeDocuments.educationSponsorship).map(
          eduDocs => {
            return <>{renderDocs(Object.values(eduDocs.documents))}</>;
          }
        )}
        {Object.values(props.financeDocuments.customRequest).map(customDocs => {
          return <>{renderDocs(Object.values(customDocs.documents))}</>;
        })} */}
      </Box>
    </>
  );
}

export default VisaPrepPreview;
